/**
 * Contract Slice — Contracts テーブルへの API ラッパ。
 * - byUnit: 区画ごとに契約一覧をキャッシュ (active / ended ともに取得)
 * - current: 詳細取得した単一契約レコード (編集中の操作対象)
 */

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import ipponApi from "@/config/axios";
import type { Contract, ContractStatus } from "@ippon/shared-types";

const BaseURL = import.meta.env.VITE_API_URL;

interface ContractState {
  byUnit: Record<
    string,
    { data: Contract[]; loading: boolean; error: string | null }
  >;
  current: { data: Contract | null; loading: boolean; error: string | null };
}

const initialState: ContractState = {
  byUnit: {},
  current: { data: null, loading: false, error: null },
};

// ── Thunks ──────────────────────────────────────────────

export const fetchContractsByUnit = createAsyncThunk<
  { unit_id: string; items: Contract[] },
  { unit_id: string; status?: ContractStatus }
>("contracts/fetchByUnit", async ({ unit_id, status }, { rejectWithValue }) => {
  try {
    const res = await ipponApi.get(`${BaseURL}/api/v1/contracts`, {
      params: { unit_id, ...(status ? { status } : {}) },
    });
    return { unit_id, items: (res.data?.data?.items || []) as Contract[] };
  } catch (e: any) {
    return rejectWithValue(e?.response?.data || "Failed to fetch contracts");
  }
});

export const fetchContract = createAsyncThunk<
  Contract,
  { contract_id: string }
>("contracts/fetchById", async ({ contract_id }, { rejectWithValue }) => {
  try {
    const res = await ipponApi.get(
      `${BaseURL}/api/v1/contracts/${contract_id}`,
    );
    return res.data?.data as Contract;
  } catch (e: any) {
    return rejectWithValue(e?.response?.data || "Failed to fetch contract");
  }
});

export const createContract = createAsyncThunk<
  Contract,
  Partial<Contract> & { unit_id: string; contractor_type: "individual" | "corporate"; contractor_name: string; start_date: string }
>("contracts/create", async (body, { rejectWithValue }) => {
  try {
    const res = await ipponApi.post(`${BaseURL}/api/v1/contracts`, body);
    return res.data?.data as Contract;
  } catch (e: any) {
    return rejectWithValue(e?.response?.data || "Failed to create contract");
  }
});

export const updateContract = createAsyncThunk<
  Contract,
  { contract_id: string; body: Partial<Contract> }
>("contracts/update", async ({ contract_id, body }, { rejectWithValue }) => {
  try {
    const res = await ipponApi.patch(
      `${BaseURL}/api/v1/contracts/${contract_id}`,
      body,
    );
    return res.data?.data as Contract;
  } catch (e: any) {
    return rejectWithValue(e?.response?.data || "Failed to update contract");
  }
});

/**
 * 顧客マスタ編集の波及確認用: 同じ customer_id を参照する **アクティブ契約** を
 * property_name / unit_name 同梱で取得する (POST /contracts/search のショートカット)。
 * 確認モーダル「この契約者は以下でも登録されています」の一覧ソース。state には載せない。
 */
export type LinkedContract = Contract & {
  property_name?: string | null;
  unit_name?: string | null;
  unit_area_type?: string | null;
};

export const searchActiveContractsByCustomer = createAsyncThunk<
  LinkedContract[],
  { customer_id: string }
>(
  "contracts/searchByCustomer",
  async ({ customer_id }, { rejectWithValue }) => {
    try {
      const res = await ipponApi.post(`${BaseURL}/api/v1/contracts/search`, {
        customer_id,
        status: "active",
      });
      return (res.data?.data?.items ?? []) as LinkedContract[];
    } catch (e: any) {
      return rejectWithValue(
        e?.response?.data || "Failed to search contracts by customer",
      );
    }
  },
);

export const endContract = createAsyncThunk<
  Contract,
  {
    contract_id: string;
    departure_date: string;
    departure_reason?: string;
    contract_remarks?: string;
  }
>("contracts/end", async ({ contract_id, ...body }, { rejectWithValue }) => {
  try {
    const res = await ipponApi.post(
      `${BaseURL}/api/v1/contracts/${contract_id}/end`,
      body,
    );
    return res.data?.data as Contract;
  } catch (e: any) {
    return rejectWithValue(e?.response?.data || "Failed to end contract");
  }
});

/**
 * 契約更新 (renewal) — 旧契約を ended にして新規 active 契約を 1 件作成。
 * BE 側で:
 *   - 既存 contract.status = "ended" + end_date 設定
 *   - 新 contract を active で発番 (旧 unit_id 引継ぎ)
 *   - Unit.current_contract_id を新 ID に置換
 *   - history を 2 件 (contract_end + renewal) で記録
 * を 1 リクエストで実行する。
 */
export const renewContract = createAsyncThunk<
  Contract,
  {
    contract_id: string;
    body: Partial<Contract> & {
      contractor_type: "individual" | "corporate";
      contractor_name: string;
      start_date: string;
    };
  }
>("contracts/renew", async ({ contract_id, body }, { rejectWithValue }) => {
  try {
    const res = await ipponApi.post(
      `${BaseURL}/api/v1/contracts/${contract_id}/renew`,
      body,
    );
    return res.data?.data as Contract;
  } catch (e: any) {
    return rejectWithValue(e?.response?.data || "Failed to renew contract");
  }
});

export const deleteContract = createAsyncThunk<
  { contract_id: string },
  { contract_id: string }
>("contracts/delete", async ({ contract_id }, { rejectWithValue }) => {
  try {
    const res = await ipponApi.delete(
      `${BaseURL}/api/v1/contracts/${contract_id}`,
    );
    return res.data?.data as { contract_id: string };
  } catch (e: any) {
    return rejectWithValue(e?.response?.data || "Failed to delete contract");
  }
});

// ── Slice ──────────────────────────────────────────────

const contractSlice = createSlice({
  name: "contracts",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // fetch by unit
    builder
      .addCase(fetchContractsByUnit.pending, (state, action) => {
        const uid = action.meta.arg.unit_id;
        state.byUnit[uid] = state.byUnit[uid] || {
          data: [],
          loading: false,
          error: null,
        };
        state.byUnit[uid].loading = true;
        state.byUnit[uid].error = null;
      })
      .addCase(fetchContractsByUnit.fulfilled, (state, action) => {
        const { unit_id, items } = action.payload;
        state.byUnit[unit_id] = { data: items, loading: false, error: null };
      })
      .addCase(fetchContractsByUnit.rejected, (state, action) => {
        const uid = action.meta.arg.unit_id;
        state.byUnit[uid] = {
          data: [],
          loading: false,
          error: (action.payload as string) || null,
        };
      });

    // fetch one
    builder
      .addCase(fetchContract.pending, (state, action) => {
        const nextId = (action.meta.arg as any)?.contract_id || null;
        const cur: any = state.current;
        // 別 contract への遷移時は前データをクリアして race を防ぐ
        if (cur.requestedId !== nextId) {
          state.current.data = null;
        }
        cur.requestedId = nextId;
        state.current.loading = true;
        state.current.error = null;
      })
      .addCase(fetchContract.fulfilled, (state, action) => {
        // race: 古い応答は無視
        const requestedId = (state.current as any).requestedId;
        const payloadId = (action.payload as any)?.contract_id;
        if (requestedId && payloadId && payloadId !== requestedId) {
          return;
        }
        state.current.data = action.payload;
        state.current.loading = false;
      })
      .addCase(fetchContract.rejected, (state, action) => {
        state.current.data = null;
        state.current.loading = false;
        state.current.error = (action.payload as string) || null;
      });

    // create / update / end — current.data に反映 + byUnit にも追記/置換
    const upsertInByUnit = (state: ContractState, c: Contract) => {
      const entry = state.byUnit[c.unit_id];
      if (!entry) return;
      const idx = entry.data.findIndex((x) => x.contract_id === c.contract_id);
      if (idx >= 0) entry.data[idx] = c;
      else entry.data = [c, ...entry.data];
    };

    builder
      .addCase(createContract.fulfilled, (state, action) => {
        state.current.data = action.payload;
        upsertInByUnit(state, action.payload);
      })
      .addCase(updateContract.fulfilled, (state, action) => {
        state.current.data = action.payload;
        upsertInByUnit(state, action.payload);
      })
      .addCase(endContract.fulfilled, (state, action) => {
        state.current.data = action.payload;
        upsertInByUnit(state, action.payload);
      })
      .addCase(renewContract.fulfilled, (state, action) => {
        // 同一 Contract レコードの上書き保存。byUnit と current を最新値で置換。
        state.current.data = action.payload;
        upsertInByUnit(state, action.payload);
      })
      .addCase(deleteContract.fulfilled, (state, action) => {
        const { contract_id } = action.payload;
        for (const uid of Object.keys(state.byUnit)) {
          state.byUnit[uid].data = state.byUnit[uid].data.filter(
            (c) => c.contract_id !== contract_id,
          );
        }
        if (state.current.data?.contract_id === contract_id) {
          state.current.data = null;
        }
      });
  },
});

export default contractSlice.reducer;
